<template>
  <v-dialog v-model="dialog" max-width="720px" scrollable>
    <v-card>
      <v-toolbar card color="grey lighten-3">
        <v-toolbar-title>Lịch sử xe</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon @click="close">
          <v-icon>close</v-icon>
        </v-btn>
      </v-toolbar>
      <v-card-text class="history-body">
        <div class="subheading mb-3">
          Biển số: <b>{{ plate || '—' }}</b>
        </div>

        <div v-if="isLoading" class="text-xs-center pa-4">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
        </div>
        <div v-else-if="errorMessage" class="text-xs-center pa-4 error--text">{{ errorMessage }}</div>
        <div v-else-if="items.length === 0" class="text-xs-center pa-4 grey--text">
          Chưa có lịch sử cho xe này
        </div>

        <div v-else class="flow">
          <template v-for="(item, i) in items">
            <div v-if="i > 0" :key="'edge-' + i" class="flow-edge">
              <v-icon small>arrow_downward</v-icon>
            </div>
            <div :key="'node-' + i" class="flow-node" :style="{ borderLeftColor: item.color }">
              <v-icon class="flow-node__icon">{{ item.icon }}</v-icon>
              <div class="flow-node__body">
                <div class="body-2 font-weight-bold">{{ item.title }}</div>
                <div class="caption">{{ item.time }}</div>
                <div v-if="item.user" class="caption">Bởi: {{ item.user }}</div>
                <div v-for="(line, n) in item.lines" :key="'line-' + n" class="caption font-italic">{{ line }}</div>
                <div v-if="item.changes.length" class="flow-detail">
                  <div v-for="(change, n) in item.changes" :key="'change-' + n" class="caption">
                    {{ change.label }}: <span class="grey--text">{{ change.oldValue }}</span> → <b>{{ change.newValue }}</b>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </div>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" flat @click="close">Đóng</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import moment from 'moment';
import { mapActions } from 'vuex';

// Event codes come from GET /vehicle/history; unknown ones fall back to a generic row.
const EVENTS = {
  Created: { title: 'Tạo xe', icon: 'add_circle', color: '#2196f3' },
  Updated: { title: 'Sửa thông tin xe', icon: 'edit', color: '#3f51b5' },
  Deleted: { title: 'Xóa xe', icon: 'delete', color: '#f44336' },
  Locked: { title: 'Khóa xe', icon: 'lock', color: '#ff9800' },
  Unlocked: { title: 'Mở khóa xe', icon: 'lock_open', color: '#4caf50' },
  Installed: { title: 'Gắn thiết bị', icon: 'settings_input_antenna', color: '#4caf50' },
  Switched: { title: 'Đổi thiết bị', icon: 'swap_horiz', color: '#009688' },
  SwappedIn: { title: 'Đổi thiết bị bảo hành', icon: 'build', color: '#f44336' },
  Removed: { title: 'Gỡ thiết bị', icon: 'link_off', color: '#ff9800' },
  AssignedToUser: { title: 'Gán xe cho người dùng', icon: 'person_add', color: '#3f51b5' },
  SecurityConfigChanged: { title: 'Đổi cấu hình bảo mật', icon: 'security', color: '#607d8b' }
};

const GENERIC_EVENT = { icon: 'history', color: '#9e9e9e' };

// Technical field names from `changes` -> Vietnamese labels.
const FIELD_LABELS = {
  Plate: 'Biển số',
  BrandName: 'Hãng xe',
  Maxspeed: 'Tốc độ tối đa',
  Remark: 'Ghi chú xe',
  VehicleType: 'Loại xe',
  ListenVibration: 'Cảnh báo rung',
  ListenMoving: 'Cảnh báo di chuyển',
  ListenMovingDistance: 'Khoảng cách di chuyển',
  ListenAcc: 'Cảnh báo ACC',
  AlarmWhistle: 'Còi báo động',
  AlarmEngineCutOff: 'Cắt máy khi báo động',
  DoorOpen: 'Cảnh báo mở cửa',
  SpeedLimit: 'Giới hạn tốc độ',
  MaxSpeed: 'Tốc độ tối đa',
  CutOffEngine: 'Cắt máy',
  PowerCut: 'Cảnh báo cắt nguồn',
  LowBattery: 'Cảnh báo pin yếu'
};

// Same convention as utils/maputil.js: 1 is a motorbike, anything else a car.
const VEHICLE_TYPES = { 1: 'Xe máy', 0: 'Ô tô' };

const EMPTY = '—';

export default {
  data: () => ({
    dialog: false,
    isLoading: false,
    plate: '',
    entries: [],
    errorMessage: '',
    requestId: 0
  }),

  computed: {
    items () {
      // The API returns newest first; show oldest first. Entries with the same time keep
      // their chronological order, which is the reverse of the API order.
      return this.entries
        .map((entry, index) => ({ entry, index }))
        .sort((a, b) => (a.entry.time || 0) - (b.entry.time || 0) || b.index - a.index)
        .map(item => this.toViewItem(item.entry));
    }
  },

  methods: {
    ...mapActions(['getVehicleHistory']),

    open (vehicle) {
      const requestId = ++this.requestId;
      this.plate = vehicle.plate;
      this.entries = [];
      this.errorMessage = '';
      this.isLoading = true;
      this.dialog = true;

      this.getVehicleHistory({ vehicleId: vehicle.id })
        .then(data => {
          if (requestId !== this.requestId) return;
          this.entries = Array.isArray(data) ? data : [];
        })
        .catch(error => {
          if (requestId !== this.requestId) return;
          if (error === 'ERR_VEHICLE_NOT_EXIST') {
            this.errorMessage = 'Xe không tồn tại hoặc đã bị xóa';
          } else if (error === 'ERR_INVALID_PERMISSION') {
            this.errorMessage = 'Bạn không có quyền xem lịch sử xe này';
          } else {
            this.errorMessage = 'Không tải được lịch sử xe';
          }
        })
        .then(() => {
          if (requestId === this.requestId) this.isLoading = false;
        });
    },

    close () {
      this.dialog = false;
    },

    toViewItem (entry) {
      const meta = EVENTS[entry.event] || GENERIC_EVENT;
      return {
        title: (EVENTS[entry.event] || { title: entry.event || 'Sự kiện không xác định' }).title,
        icon: meta.icon,
        color: meta.color,
        time: entry.time ? moment(entry.time).format('DD/MM/YYYY HH:mm:ss') : EMPTY,
        user: this.userOf(entry),
        lines: this.linesOf(entry),
        changes: this.changesOf(entry)
      };
    },

    // "Name - phone"; Created has no actor, so it stays empty.
    userOf (entry) {
      if (!entry.userName && !entry.userPhone) return '';
      return entry.userPhone ? `${entry.userName || ''} - ${entry.userPhone}` : entry.userName;
    },

    linesOf (entry) {
      const lines = [];
      switch (entry.event) {
        case 'Installed':
          if (entry.deviceImei) lines.push(`Thiết bị: IMEI ${entry.deviceImei}`);
          if (entry.previousDeviceImei) lines.push(`Thiết bị trước đó: IMEI ${entry.previousDeviceImei}`);
          break;
        case 'Switched':
        case 'SwappedIn':
          if (entry.deviceImei) lines.push(`Thiết bị mới: IMEI ${entry.deviceImei}`);
          if (entry.previousDeviceImei) lines.push(`Thiết bị cũ: IMEI ${entry.previousDeviceImei}`);
          break;
        case 'Removed':
          if (entry.deviceImei) lines.push(`Thiết bị bị gỡ: IMEI ${entry.deviceImei}`);
          break;
        case 'AssignedToUser':
          if (entry.targetUserName) lines.push(`Người dùng: ${entry.targetUserName}`);
          break;
        default:
      }
      if (entry.remark) lines.push(`Ghi chú: ${entry.remark}`);
      return lines;
    },

    changesOf (entry) {
      if (!Array.isArray(entry.changes)) return [];
      return entry.changes.map(change => ({
        label: FIELD_LABELS[change.field] || change.field,
        // oldValue null means "unknown before", not "empty".
        oldValue: this.formatValue(change.field, change.oldValue),
        newValue: this.formatValue(change.field, change.newValue)
      }));
    },

    formatValue (field, value) {
      if (value === null || value === undefined || value === '') return EMPTY;
      if (value === true || value === 'true') return 'Bật';
      if (value === false || value === 'false') return 'Tắt';
      if (field === 'VehicleType') return VEHICLE_TYPES[value] || String(value);
      return String(value);
    }
  }
};
</script>

<style scoped>
.history-body {
  max-height: 70vh;
}

.flow {
  padding: 4px 0 8px;
}

.flow-node {
  display: flex;
  align-items: flex-start;
  padding: 10px 16px;
  border-left: 4px solid #9e9e9e;
  border-radius: 4px;
  background: #fafafa;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
}

.flow-node__icon {
  margin-right: 12px;
  margin-top: 2px;
}

.flow-node__body {
  flex: 1;
}

.flow-detail {
  margin-top: 6px;
  padding: 6px 10px;
  background: #fff;
  border-left: 2px solid #bdbdbd;
}

.flow-edge {
  display: flex;
  align-items: center;
  margin: 4px 0 4px 18px;
  font-size: 12px;
  color: #757575;
}
</style>



// WEBPACK FOOTER //
// src/pages/vehicle/components/DialogVehicleHistory.vue